import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

const CONSENT_KEY = 'pezente_cookie_consent'; // { value: 'accepted' | 'rejected', timestamp }
const POSTHOG_KEY = 'phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs';
const CONSENT_TTL_MS = 13 * 30 * 24 * 60 * 60 * 1000; // ~13 meses, alinhado à Privacy Policy

// Nome do evento customizado que qualquer parte do app (ex: link no Footer)
// pode disparar para reabrir o banner, mesmo com consentimento já salvo.
export const REOPEN_COOKIE_BANNER_EVENT = 'pezente:open-cookie-preferences';

function readStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.timestamp) return null;
    const expired = Date.now() - parsed.timestamp > CONSENT_TTL_MS;
    return expired ? null : parsed.value;
  } catch {
    return null;
  }
}

function storeConsent(value) {
  localStorage.setItem(
    CONSENT_KEY,
    JSON.stringify({ value, timestamp: Date.now() })
  );
}

/**
 * Carrega e inicializa o PostHog em runtime — só é chamado após
 * consentimento explícito do usuário (opt-in real).
 * Este é o mesmo snippet que estava fixo no index.html; movido para cá
 * para não rodar automaticamente no carregamento da página.
 */
function loadPostHog() {
  if (window.posthog && window.posthog.__loaded) return; // evita carregar 2x

  (function (t, e) {
    var o, n, p, r;
    e.__SV ||
      ((window.posthog = e),
      (e._i = []),
      (e.init = function (i, s, a) {
        function g(t, e) {
          var o = e.split('.');
          2 == o.length && ((t = t[o[0]]), (e = o[1])),
            (t[e] = function () {
              t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
            });
        }
        ((p = t.createElement('script')).type = 'text/javascript'),
          (p.crossOrigin = 'anonymous'),
          (p.async = !0),
          (p.src =
            s.api_host.replace('.i.posthog.com', '-assets.i.posthog.com') +
            '/static/array.js'),
          (r = t.getElementsByTagName('script')[0]).parentNode.insertBefore(p, r);
        var u = e;
        for (
          void 0 !== a ? (u = e[a] = []) : (a = 'posthog'),
            (u.people = u.people || []),
            (u.toString = function (t) {
              var e = 'posthog';
              return ('posthog' !== a && (e += '.' + a), t || (e += ' (stub)'), e);
            }),
            (u.people.toString = function () {
              return u.toString(1) + '.people (stub)';
            }),
            (o =
              'init me ws ys ps bs capture je Di ks register register_once register_for_session unregister unregister_for_session Ps getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Es $s createPersonProfile Is opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing Ss debug xs getPageViewId captureTraceFeedback captureTraceMetric'.split(
                ' '
              ),
            n = 0);
          n < o.length;
          n++
        )
          g(u, o[n]);
        e._i.push([i, s, a]);
      }),
      (e.__SV = 1));
  })(document, window.posthog || []);

  window.posthog.init(POSTHOG_KEY, {
    api_host: 'https://us.i.posthog.com',
    person_profiles: 'identified_only',
    session_recording: {
      recordCrossOriginIframes: false,
      capturePerformance: false,
    },
  });

  window.posthog.__loaded = true;
}

/**
 * Desativa o PostHog em runtime quando o usuário recusa ou revoga o
 * consentimento depois de já ter aceitado antes (via link "Cookie
 * Preferences" no footer).
 */
function unloadPostHog() {
  if (window.posthog && typeof window.posthog.opt_out_capturing === 'function') {
    window.posthog.opt_out_capturing();
  }
}

export default function CookieConsentBanner() {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = readStoredConsent();

    if (consent === 'accepted') {
      loadPostHog();
    } else if (consent !== 'rejected') {
      // Nunca decidiu antes, ou o consentimento expirou (>13 meses) ->
      // mostra o banner. Nada é carregado até aqui.
      setVisible(true);
    }

    // Permite reabrir o banner a qualquer momento (ex: link no Footer),
    // mesmo com um consentimento válido já salvo.
    const handleReopen = () => setVisible(true);
    window.addEventListener(REOPEN_COOKIE_BANNER_EVENT, handleReopen);
    return () => window.removeEventListener(REOPEN_COOKIE_BANNER_EVENT, handleReopen);
  }, []);

  const handleAccept = () => {
    storeConsent('accepted');
    loadPostHog();
    setVisible(false);
  };

  const handleReject = () => {
    storeConsent('rejected');
    unloadPostHog();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed bottom-0 left-0 right-0 z-[999] card-bg border-t"
      style={{ borderColor: 'rgba(232,200,136,0.15)' }}
    >
      <div className="max-w-6xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="text-xs sm:text-sm text-[#9CA3AF] leading-relaxed max-w-2xl">
          {t.cookies.message}{' '}
          <a
            href="/privacy-policy.html"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#D4AF7A] underline hover:text-[#E8C888] transition-colors"
          >
            {t.cookies.policyLinkText}
          </a>
          .
        </p>

        <div className="flex gap-3 flex-shrink-0 w-full sm:w-auto">
          <button
            type="button"
            onClick={handleReject}
            className="flex-1 sm:flex-none px-6 py-3 text-xs tracking-[0.2em] font-semibold rounded-md border border-[rgba(232,200,136,0.3)] text-[#E8EAF0] hover:bg-[rgba(232,200,136,0.08)] transition-colors"
          >
            {t.cookies.reject}
          </button>
          <button
            type="button"
            onClick={handleAccept}
            className="gold-btn flex-1 sm:flex-none px-6 py-3 text-xs tracking-[0.2em] font-semibold rounded-md"
          >
            {t.cookies.accept}
          </button>
        </div>
      </div>
    </div>
  );
}
